import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useSearchParams, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/hooks/useAuth';
import educationPattern from '@/assets/education-pattern.webp';
import unlockedLogo from '@/assets/unlocked-logo.webp';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { Loader2, ArrowLeft, ShieldAlert } from 'lucide-react';
import { AppRole } from '@/lib/supabase-types';
import { signUpSchema, signInSchema, emailSchema } from '@/lib/validation';
import { scaleIn, appleEase } from '@/lib/animations';
import { getAuthErrorMessage } from '@/lib/auth-errors';
import { SocialLoginButtons } from '@/components/auth/SocialLoginButtons';
import { Seo } from '@/components/Seo';
import { OrDivider } from '@/components/auth/OrDivider';
import { supabase } from '@/integrations/supabase/client';
import { authReturnUrl, sanitizeNextPath } from '@/lib/auth-redirect';

export default function Auth() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { signIn, signUp, resetPassword, signOut, user, userRole, loading, setUserRole } = useAuth();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAssigningRole, setIsAssigningRole] = useState(false);
  
  const [isCheckingExistingRole, setIsCheckingExistingRole] = useState(false);
  const roleCheckTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Where to send the user after auth (e.g. the OAuth consent screen). Same-origin only.
  const nextPath = sanitizeNextPath(searchParams.get('next'));
  const returnUrl = authReturnUrl(nextPath);

  const [signInData, setSignInData] = useState({ email: '', password: '' });
  const [signUpData, setSignUpData] = useState({ email: '', password: '', fullName: '' });
  
  const [signInErrors, setSignInErrors] = useState<{ email?: string; password?: string }>({});
  const [signUpErrors, setSignUpErrors] = useState<{ email?: string; password?: string; fullName?: string }>({});
  const [forgotPasswordError, setForgotPasswordError] = useState<string | undefined>();

  // Login lockout state (exponential backoff)
  const [lockoutRetryAfter, setLockoutRetryAfter] = useState<number | null>(null);
  const [lockoutCountdown, setLockoutCountdown] = useState<number>(0);

  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordSubmitting, setForgotPasswordSubmitting] = useState(false);

  const pendingRole = sessionStorage.getItem('pending_role') as AppRole | null;
  const pendingPlan = localStorage.getItem('pending_plan') as 'free' | 'pro' | 'family' | null;

  // Inline blur validators
  const validateSignInField = (field: 'email' | 'password') => {
    const result = signInSchema.safeParse(signInData);
    if (!result.success) {
      const errors = result.error.flatten().fieldErrors;
      if (field === 'email' && errors.email) {
        setSignInErrors(prev => ({ ...prev, email: errors.email?.[0] }));
      }
      if (field === 'password' && errors.password) {
        setSignInErrors(prev => ({ ...prev, password: errors.password?.[0] }));
      }
    }
  };

  const validateSignUpField = (field: 'email' | 'password' | 'fullName') => {
    const result = signUpSchema.safeParse(signUpData);
    if (!result.success) {
      const errors = result.error.flatten().fieldErrors;
      if (field === 'email' && errors.email) {
        setSignUpErrors(prev => ({ ...prev, email: errors.email?.[0] }));
      }
      if (field === 'password' && errors.password) {
        setSignUpErrors(prev => ({ ...prev, password: errors.password?.[0] }));
      }
      if (field === 'fullName' && errors.fullName) {
        setSignUpErrors(prev => ({ ...prev, fullName: errors.fullName?.[0] }));
      }
    }
  };

  const validateForgotPasswordField = () => {
    const result = emailSchema.safeParse(forgotPasswordEmail.trim());
    if (!result.success) {
      setForgotPasswordError(result.error.errors[0]?.message);
    }
  };

  useEffect(() => {
    if (searchParams.get('reason') === 'timeout') {
      toast({
        title: 'Session expired',
        description: 'You were signed out due to inactivity. Please sign in again.',
        variant: 'default',
      });
      searchParams.delete('reason');
      setSearchParams(searchParams, { replace: true });
    }
  }, []);

  useEffect(() => {
    if (searchParams.get('fresh') === 'true') {
      localStorage.removeItem('sb-lweauudwylsfnlcftrjz-auth-token');
      sessionStorage.removeItem('pending_role');
      signOut().finally(() => {
        searchParams.delete('fresh');
        setSearchParams(searchParams, { replace: true });
        window.location.href = '/auth';
      });
    }
  }, []);

  useEffect(() => {
    if (nextPath && user && !loading) {
      navigate(nextPath, { replace: true });
    }
  }, [nextPath, user, loading, navigate]);

  useEffect(() => {
    if (nextPath) return;
    if (user && !userRole && !pendingRole && !loading && !isAssigningRole && !isCheckingExistingRole) {
      setIsCheckingExistingRole(true);
      roleCheckTimeoutRef.current = setTimeout(() => {
        setIsCheckingExistingRole(false);
      }, 3000);
    }
    return () => {
      if (roleCheckTimeoutRef.current) clearTimeout(roleCheckTimeoutRef.current);
    };
  }, [user, pendingRole, loading, isAssigningRole]);
  
  useEffect(() => {
    if (nextPath) return;
    if (userRole && isCheckingExistingRole) {
      if (roleCheckTimeoutRef.current) clearTimeout(roleCheckTimeoutRef.current);
      setIsCheckingExistingRole(false);
      if (userRole === 'student') navigate('/student', { replace: true });
      else if (userRole === 'parent') navigate('/parent', { replace: true });
      else if (userRole === 'admin') navigate('/admin', { replace: true });
    }
  }, [userRole, isCheckingExistingRole, navigate]);

  useEffect(() => {
    const assignPendingRole = async () => {
      if (nextPath) return;
      if (user && !userRole && pendingRole && !isAssigningRole && !loading) {
        setIsAssigningRole(true);
        const { error } = await setUserRole(pendingRole);
        
        if (error) {
          if (error.message.includes('session has expired')) {
            toast({ title: 'Session expired', description: 'Please sign up again.', variant: 'destructive' });
            setIsAssigningRole(false);
            window.location.href = '/role-select';
            return;
          }
          sessionStorage.removeItem('pending_role');
          localStorage.removeItem('pending_plan');
          toast({ title: 'Error setting role', description: error.message, variant: 'destructive' });
          setIsAssigningRole(false);
        } else {
          sessionStorage.removeItem('pending_role');
          if (pendingRole === 'student') navigate('/student/onboarding');
          else if (pendingRole === 'parent') navigate('/parent/onboarding');
        }
      } else if (user && userRole && pendingRole && !loading) {
        sessionStorage.removeItem('pending_role');
        localStorage.removeItem('pending_plan');
        if (userRole === 'student') navigate('/student');
        else if (userRole === 'parent') navigate('/parent');
        else if (userRole === 'admin') navigate('/admin');
      }
    };
    assignPendingRole();
  }, [user, userRole, pendingRole, pendingPlan, isAssigningRole, loading, setUserRole, navigate, toast]);

  useEffect(() => {
    if (nextPath) return;
    if (user && userRole && !loading) {
      if (userRole === 'student') navigate('/student');
      else if (userRole === 'parent') navigate('/parent');
      else if (userRole === 'admin') navigate('/admin');
    }
  }, [user, userRole, loading, navigate]);

  useEffect(() => {
    if (nextPath) return;
    if (user && !userRole && !pendingRole && !loading && !isAssigningRole && !isCheckingExistingRole) {
      const bypass = searchParams.get('bypass');
      navigate(`/role-select${bypass === 'true' ? '?bypass=true' : ''}`);
    }
  }, [user, userRole, pendingRole, loading, isAssigningRole, isCheckingExistingRole, navigate]);
  // Countdown timer for lockout
  useEffect(() => {
    if (lockoutRetryAfter === null || lockoutRetryAfter <= 0) {
      setLockoutCountdown(0);
      return;
    }
    setLockoutCountdown(lockoutRetryAfter);
    const interval = setInterval(() => {
      setLockoutCountdown(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          setLockoutRetryAfter(null);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [lockoutRetryAfter]);

  const checkLoginLockout = async (email: string): Promise<boolean> => {
    try {
      const { data, error } = await supabase.functions.invoke('rate-limit-check', {
        body: { action: 'check', identifier: email },
      });
      if (error) return false;
      if (data?.locked) {
        setLockoutRetryAfter(data.retryAfter ?? 300);
        return true;
      }
      return false;
    } catch {
      return false;
    }
  };

  const recordLoginResult = async (email: string, success: boolean) => {
    try {
      await supabase.functions.invoke('rate-limit-check', {
        body: { action: 'record', identifier: email, success },
      });
    } catch {
      // Non-fatal
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    const result = signInSchema.safeParse(signInData);
    if (!result.success) {
      const errors = result.error.flatten().fieldErrors;
      setSignInErrors({ email: errors.email?.[0], password: errors.password?.[0] });
      return;
    }
    setSignInErrors({});

    // Check lockout before attempting login
    const isLocked = await checkLoginLockout(result.data.email);
    if (isLocked) return;

    setIsSubmitting(true);
    const { error } = await signIn(result.data.email, result.data.password);
    setIsSubmitting(false);
    if (error) {
      // Record failed attempt for lockout tracking
      await recordLoginResult(result.data.email, false);
      const friendlyError = getAuthErrorMessage(error);
      toast({ title: friendlyError.title, description: friendlyError.description, variant: 'destructive' });
    } else {
      // Record success to reset failure counter
      await recordLoginResult(result.data.email, true);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    const result = signUpSchema.safeParse(signUpData);
    if (!result.success) {
      const errors = result.error.flatten().fieldErrors;
      setSignUpErrors({ email: errors.email?.[0], password: errors.password?.[0], fullName: errors.fullName?.[0] });
      return;
    }
    setSignUpErrors({});
    setIsSubmitting(true);
    const { error } = await signUp(result.data.email, result.data.password, result.data.fullName, returnUrl);
    setIsSubmitting(false);
    if (error) {
      const friendlyError = getAuthErrorMessage(error);
      toast({ title: friendlyError.title, description: friendlyError.description, variant: 'destructive' });
    } else {
      toast({ title: 'Account created!', description: "Welcome to UnlockEd Tutoring. Let's get you set up." });
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    const emailResult = emailSchema.safeParse(forgotPasswordEmail.trim());
    if (!emailResult.success) {
      setForgotPasswordError(emailResult.error.errors[0]?.message || 'Please enter a valid email address.');
      return;
    }
    setForgotPasswordError(undefined);
    setForgotPasswordSubmitting(true);
    const { error } = await resetPassword(forgotPasswordEmail.trim());
    setForgotPasswordSubmitting(false);
    if (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } else {
      toast({ title: 'Check your email', description: "If an account exists with this email, you'll receive a password reset link." });
      setShowForgotPassword(false);
      setForgotPasswordEmail('');
    }
  };

  if ((loading || isAssigningRole || isCheckingExistingRole) && !userRole) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-accent mx-auto mb-4" />
          <p className="text-muted-foreground">
            {isAssigningRole ? 'Setting up your account...' : isCheckingExistingRole ? 'Checking your account...' : 'Loading...'}
          </p>
        </div>
      </div>
    );
  }

  // Reusable sign-in form fields
  const renderSignInFields = (idPrefix: string) => (
    <>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-email`}>Email</Label>
        <Input
          id={`${idPrefix}-email`}
          data-testid="signin-email-input"
          type="email"
          placeholder="you@example.com"
          value={signInData.email}
          onChange={(e) => {
            setSignInData({ ...signInData, email: e.target.value });
            if (signInErrors.email) setSignInErrors({ ...signInErrors, email: undefined });
          }}
          onBlur={() => validateSignInField('email')}
          className={signInErrors.email ? 'border-destructive' : ''}
          autoComplete="email"
          aria-required="true"
          aria-invalid={!!signInErrors.email}
          aria-describedby={signInErrors.email ? `${idPrefix}-email-error` : undefined}
        />
        {signInErrors.email && (
          <p id={`${idPrefix}-email-error`} className="text-sm text-destructive" role="alert">{signInErrors.email}</p>
        )}
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-password`}>Password</Label>
        <PasswordInput
          id={`${idPrefix}-password`}
          data-testid="signin-password-input"
          placeholder="••••••••"
          value={signInData.password}
          onChange={(e) => {
            setSignInData({ ...signInData, password: e.target.value });
            if (signInErrors.password) setSignInErrors({ ...signInErrors, password: undefined });
          }}
          onBlur={() => validateSignInField('password')}
          className={signInErrors.password ? 'border-destructive' : ''}
          autoComplete="current-password"
          aria-required="true"
          aria-invalid={!!signInErrors.password}
          aria-describedby={signInErrors.password ? `${idPrefix}-password-error` : undefined}
        />
        {signInErrors.password && (
          <p id={`${idPrefix}-password-error`} className="text-sm text-destructive" role="alert">{signInErrors.password}</p>
        )}
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-background flex flex-col relative">
      <Seo
        title="Sign in — UnlockEd Tutoring"
        description="Sign in or create your UnlockEd Tutoring account to access TASC-aligned AI tutoring."
        path="/auth"
        noindex
      />
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: `url(${educationPattern})`,
          backgroundSize: '800px',
          backgroundRepeat: 'repeat',
          opacity: 0.12,
        }}
      />
      <header className="border-b bg-background/80 backdrop-blur-md relative z-10">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>
          <Link to="/" className="flex items-center gap-2">
            <div className="w-16 h-16 rounded-xl overflow-hidden">
              <img src={unlockedLogo} alt="UnlockEd Tutoring" className="w-full h-full object-cover" />
            </div>
            <span className="font-display font-bold text-2xl">UnlockEd Tutoring</span>
          </Link>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 py-8 relative z-10">
        <h1 className="sr-only">
          {pendingRole === 'student'
            ? 'Create your UnlockEd Tutoring student account'
            : pendingRole === 'parent'
              ? 'Sign in to your UnlockEd Tutoring parent account'
              : 'Sign in or create your UnlockEd Tutoring account'}
        </h1>
        <motion.div initial="hidden" animate="visible" variants={scaleIn}>
        <Card className="w-full max-w-md border shadow-lg">
          <CardHeader className="text-center">
            <CardTitle className="font-display text-2xl">
              {pendingRole ? `Sign ${pendingRole === 'student' ? 'up' : 'in'} as ${pendingRole === 'student' ? 'Student' : 'Parent'}` : 'Welcome Back'}
            </CardTitle>
            <CardDescription>
              {pendingRole 
                ? `Create your ${pendingRole} account to get started`
                : 'Sign in to continue your learning journey'
              }
            </CardDescription>
          </CardHeader>

          <CardContent>
          {pendingRole ? (
            <>
            <SocialLoginButtons onBeforeSignIn={() => {}} labelPrefix="Continue with" redirectUrl={returnUrl} />
            <OrDivider />
            <Tabs defaultValue="signup" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="signin">Sign In</TabsTrigger>
                <TabsTrigger value="signup">Sign Up</TabsTrigger>
              </TabsList>

              <TabsContent value="signin">
                <form onSubmit={handleSignIn} className="space-y-4" noValidate>
                  {renderSignInFields('tab-signin')}
                  <div className="flex justify-end">
                    <button type="button" onClick={() => setShowForgotPassword(true)} className="text-sm text-accent hover:underline">
                      Forgot password?
                    </button>
                  </div>
                  {lockoutCountdown > 0 && (
                    <div className="flex items-center gap-2 p-3 rounded-md bg-destructive/10 border border-destructive/20 text-destructive text-sm" role="alert">
                      <ShieldAlert className="w-4 h-4 shrink-0" />
                      <span>Too many failed attempts. Try again in <strong>{lockoutCountdown}s</strong>.</span>
                    </div>
                  )}
                  <Button type="submit" variant="accent" className="w-full" disabled={isSubmitting || lockoutCountdown > 0}>
                    {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Sign In
                  </Button>
                </form>
              </TabsContent>

              <TabsContent value="signup">
                <form onSubmit={handleSignUp} className="space-y-4" noValidate>
                  <div className="space-y-2">
                    <Label htmlFor="signup-name">Full Name</Label>
                    <Input
                      id="signup-name"
                      type="text"
                      placeholder="Your name"
                      value={signUpData.fullName}
                      onChange={(e) => {
                        setSignUpData({ ...signUpData, fullName: e.target.value });
                        if (signUpErrors.fullName) setSignUpErrors({ ...signUpErrors, fullName: undefined });
                      }}
                      onBlur={() => validateSignUpField('fullName')}
                      className={signUpErrors.fullName ? 'border-destructive' : ''}
                      autoComplete="name"
                      aria-required="true"
                      aria-invalid={!!signUpErrors.fullName}
                      aria-describedby={signUpErrors.fullName ? 'signup-name-error' : undefined}
                    />
                    {signUpErrors.fullName && (
                      <p id="signup-name-error" className="text-sm text-destructive" role="alert">{signUpErrors.fullName}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-email">Email</Label>
                    <Input
                      id="signup-email"
                      type="email"
                      placeholder="you@example.com"
                      value={signUpData.email}
                      onChange={(e) => {
                        setSignUpData({ ...signUpData, email: e.target.value });
                        if (signUpErrors.email) setSignUpErrors({ ...signUpErrors, email: undefined });
                      }}
                      onBlur={() => validateSignUpField('email')}
                      className={signUpErrors.email ? 'border-destructive' : ''}
                      autoComplete="email"
                      aria-required="true"
                      aria-invalid={!!signUpErrors.email}
                      aria-describedby={signUpErrors.email ? 'signup-email-error' : undefined}
                    />
                    {signUpErrors.email && (
                      <p id="signup-email-error" className="text-sm text-destructive" role="alert">{signUpErrors.email}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-password">Password</Label>
                    <PasswordInput
                      id="signup-password"
                      placeholder="••••••••"
                      value={signUpData.password}
                      onChange={(e) => {
                        setSignUpData({ ...signUpData, password: e.target.value });
                        if (signUpErrors.password) setSignUpErrors({ ...signUpErrors, password: undefined });
                      }}
                      onBlur={() => validateSignUpField('password')}
                      className={signUpErrors.password ? 'border-destructive' : ''}
                      autoComplete="new-password"
                      aria-required="true"
                      aria-invalid={!!signUpErrors.password}
                      aria-describedby={signUpErrors.password ? 'signup-password-error' : undefined}
                    />
                    {signUpErrors.password && (
                      <p id="signup-password-error" className="text-sm text-destructive" role="alert">{signUpErrors.password}</p>
                    )}
                  </div>
                  <Button type="submit" variant="accent" className="w-full" disabled={isSubmitting}>
                    {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Create Account
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
            </>
          ) : (
            <div>
              <SocialLoginButtons labelPrefix="Sign in with" redirectUrl={returnUrl} />
              <OrDivider />
              <form onSubmit={handleSignIn} className="space-y-4" noValidate>
                {renderSignInFields('main-signin')}
                <div className="flex justify-end">
                  <button type="button" onClick={() => setShowForgotPassword(true)} className="text-sm text-accent hover:underline">
                    Forgot password?
                  </button>
                </div>
                {lockoutCountdown > 0 && (
                  <div className="flex items-center gap-2 p-3 rounded-md bg-destructive/10 border border-destructive/20 text-destructive text-sm" role="alert">
                    <ShieldAlert className="w-4 h-4 shrink-0" />
                    <span>Too many failed attempts. Try again in <strong>{lockoutCountdown}s</strong>.</span>
                  </div>
                )}
                <Button type="submit" variant="accent" className="w-full" disabled={isSubmitting || lockoutCountdown > 0}>
                  {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Sign In
                </Button>
              </form>
            </div>
          )}
          </CardContent>

          <CardFooter className="text-center text-sm text-muted-foreground">
            <p className="w-full">
              By continuing, you agree to our{' '}
              <Link to="/terms" className="text-accent hover:underline">Terms</Link>
              {' '}and{' '}
              <Link to="/privacy" className="text-accent hover:underline">Privacy Policy</Link>
            </p>
          </CardFooter>
        </Card>
        </motion.div>
      </main>

      {/* Forgot Password Dialog */}
      <Dialog open={showForgotPassword} onOpenChange={(open) => { setShowForgotPassword(open); if (!open) setForgotPasswordError(undefined); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>
              Enter your email address and we'll send you a link to reset your password.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleForgotPassword} className="space-y-4" noValidate>
            <div className="space-y-2">
              <Label htmlFor="forgot-email">Email</Label>
              <Input
                id="forgot-email"
                type="email"
                placeholder="you@example.com"
                value={forgotPasswordEmail}
                onChange={(e) => {
                  setForgotPasswordEmail(e.target.value);
                  if (forgotPasswordError) setForgotPasswordError(undefined);
                }}
                onBlur={validateForgotPasswordField}
                autoComplete="email"
                aria-required="true"
                aria-invalid={!!forgotPasswordError}
                aria-describedby={forgotPasswordError ? 'forgot-email-error' : undefined}
              />
              {forgotPasswordError && (
                <p id="forgot-email-error" className="text-sm text-destructive" role="alert">{forgotPasswordError}</p>
              )}
            </div>
            <div className="flex gap-3 justify-end">
              <Button type="button" variant="outline" onClick={() => setShowForgotPassword(false)}>Cancel</Button>
              <Button type="submit" disabled={forgotPasswordSubmitting}>
                {forgotPasswordSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Send Reset Link
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
